import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { usePricing } from "../hooks/usePricing";
import { PriceAnchor } from "../components/PriceAnchor";
import { TrustStrip } from "../components/TrustStrip";
import { SocialProof } from "../components/SocialProof";

const SHOWCASE = [
  { name: "wedding_1920s", caption: "1920s wedding" },
  { name: "family_1940s",  caption: "1940s family" },
  { name: "child_1960s",   caption: "1960s summer" },
  { name: "portrait_1950s", caption: "1950s portrait" },
];

const OCCASIONS = [
  {
    eyebrow: "In loving memory",
    title: "For the ones we've lost.",
    text: "A moment you can watch again — the smile, the movement, the warmth. For a memorial, an anniversary of their passing, or simply because you miss them today.",
  },
  {
    eyebrow: "Mother's & Father's Day",
    title: "The gift they never expected.",
    text: "A photograph from their childhood, brought back to life. They will play it, and play it, and play it. No one else will think to give them this.",
  },
  {
    eyebrow: "Milestone anniversary",
    title: "The day you first met.",
    text: "Fifty years later, watch that afternoon move again — the light, the look, the life between you. A gift for a wedding anniversary, a vow renewal, a quiet dinner at home.",
  },
  {
    eyebrow: "A birthday to remember",
    title: "Take them back, just for a minute.",
    text: "For a 70th, an 80th, a 90th. Share the moment at the table and watch the room go quiet. It is the most personal gift you will ever give.",
  },
];

export function Landing() {
  const { email } = useAuth();
  const { display: price } = usePricing();
  return (
    <>
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pt-16 pb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="/logo/mark_bloom.png"
              alt=""
              aria-hidden
              className="h-28 w-28 sm:h-32 sm:w-32 object-contain mb-4"
            />
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.05]">
              Bring your old photos
              <br />
              <span className="italic text-neutral-700">back to life.</span>
            </h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-lg">
              Watch them smile again. Watch the afternoon move. We take one
              photograph you love — a parent, a grandparent, a day you wish you
              could step back into — and turn it into a short cinematic video you
              can keep forever, or give to the people who need it most.
            </p>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <Link
                to={email ? "/upload" : "/signup"}
                className="inline-flex items-center gap-2 rounded-full bg-black text-white px-7 py-3.5 text-base font-medium hover:bg-neutral-800 transition"
              >
                Give a memory — {price}
              </Link>
              <PriceAnchor compact />
            </div>
            <SocialProof className="mt-4" />
            <TrustStrip className="mt-4 justify-start" />
          </div>

          <figure className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-amber-100/60 to-rose-100/40 blur-xl" aria-hidden />
            <img
              src="/showcase/hero.jpg"
              alt="A warm 1940s family scene on a summer porch"
              className="relative rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)] border border-neutral-200 w-full h-auto"
              loading="eager"
            />
            <figcaption className="mt-3 text-xs text-neutral-400 text-center italic">
              A moment, brought back — the kind we make every day.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Video showcase — led by the gift-worth-keeping hook */}
      <section className="bg-white/60 border-y border-neutral-200/70">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-neutral-500">A gift worth keeping</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.1]">
              For the moments that deserve more than a frame.
            </h2>
            <p className="mt-4 text-lg text-neutral-600">
              Most gifts are forgotten by next year. This one gets watched every birthday,
              every anniversary, every time someone asks to see them again.
            </p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 gap-8">
            {SHOWCASE.map((item) => (
              <VideoCard key={item.name} name={item.name} caption={item.caption} />
            ))}
          </div>

          <p className="mt-10 text-center text-sm text-neutral-500 italic">
            Real videos made with back2life. Cinematic motion, period-appropriate music.
          </p>
        </div>
      </section>

      {/* Emotional pull-quote */}
      <section className="mx-auto max-w-3xl px-6 py-24 text-center">
        <p className="text-xs uppercase tracking-widest text-neutral-500">Why people make one</p>
        <blockquote className="mt-6 font-serif text-3xl sm:text-4xl leading-snug tracking-tight text-neutral-800">
          &ldquo;My mum cried when she saw her dad walking again.
          <span className="block italic text-neutral-600 mt-2">Thirty years gone, and there he was.&rdquo;</span>
        </blockquote>
        <p className="mt-6 text-sm text-neutral-500">— a gift for a 70th birthday</p>
      </section>

      {/* Occasions — gift/memorial framing */}
      <section className="bg-white/60 border-y border-neutral-200/70">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-neutral-500">Who it's for</p>
            <h2 className="mt-3 font-serif text-4xl tracking-tight">
              Every family has one of these moments.
            </h2>
            <p className="mt-4 text-neutral-600">
              Pick the one that matters to yours.
            </p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 gap-8">
            {OCCASIONS.map((o) => (
              <OccasionCard key={o.title} {...o} />
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to={email ? "/upload" : "/signup"}
              className="inline-flex items-center gap-2 rounded-full bg-black text-white px-7 py-3.5 text-base font-medium hover:bg-neutral-800 transition"
            >
              Make one — {price}
            </Link>
            <div className="mt-4 flex justify-center">
              <PriceAnchor align="center" />
            </div>
            <p className="mt-4 text-sm text-neutral-500">
              No subscription. Delivered in minutes. Yours to keep, forever.
            </p>
            <TrustStrip className="mt-6" />
          </div>
        </div>
      </section>

      {/* Before / after */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="text-center max-w-xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-neutral-500">The transformation</p>
          <h2 className="mt-3 font-serif text-4xl tracking-tight">Before. After.</h2>
          <p className="mt-3 text-neutral-600">
            Restoration and colorisation are the first step. The video follows.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 gap-8">
          {SHOWCASE.map((item) => (
            <BeforeAfter key={item.name} name={item.name} caption={item.caption} />
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <div className="grid sm:grid-cols-3 gap-6">
          <Step n="1" title="Upload" text="Pick a scanned photo from your phone or computer." />
          <Step n="2" title="We craft" text="AI restores, colorises, and directs a short cinematic scene." />
          <Step n="3" title="Share" text="Download the MP4 or send it to someone who needs to see it." />
        </div>
        <div className="mt-12 text-center">
          <Link
            to={email ? "/upload" : "/signup"}
            className="inline-flex items-center gap-2 rounded-full bg-black text-white px-7 py-3.5 text-base font-medium hover:bg-neutral-800 transition"
          >
            Give a memory — {price}
          </Link>
          <p className="mt-3 text-sm text-neutral-400">
            For someone you love. Or for yourself.
          </p>
        </div>
      </section>

      {/* FAQ — defuse the real fears */}
      <section className="bg-white/60 border-t border-neutral-200/70">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest text-neutral-500">Questions</p>
            <h2 className="mt-3 font-serif text-4xl tracking-tight">Before you upload.</h2>
          </div>

          <div className="mt-10 divide-y divide-neutral-200 rounded-2xl border border-neutral-200 bg-white">
            <Faq q="Will it actually look like the person I love?">
              The AI works from the face in your photo — the proportions, the
              hair, the smile. It's recognisably them. If a particular take
              doesn't quite capture it, you have <strong>2 free re-dos</strong>{" "}
              included with every paid video, so you can pick the version that
              feels right.
            </Faq>
            <Faq q="What happens to my photo? Do you keep it?">
              Your original photo is stored only long enough to create your
              video, then automatically deleted from our servers after{" "}
              <strong>7 days</strong>. We don't use your photos to train AI
              models, and we never share them. Full details in our{" "}
              <Link to="/privacy" className="underline">privacy policy</Link>.
            </Faq>
            <Faq q="What if the result looks weird?">
              Use one of your 2 free re-dos. Each re-do varies the camera and
              motion — same face, same colours, different scene. If something
              has clearly broken (a black frame, distorted features, wrong
              person), email us and we'll regenerate or refund.
            </Faq>
            <Faq q="Can I use any photo?">
              Yes — old, scanned, faded, black-and-white, even slightly
              damaged. We restore and colorise before animating. Works best
              with a clear face and decent lighting in the original. Group
              shots work, but the AI focuses on the most prominent face.
            </Faq>
            <Faq q="How long does it take?">
              A few minutes from payment. We'll email you when it's ready and
              you can also track it live on the site.
            </Faq>
            <Faq q="Can I share it? Print it? Post it on Instagram?">
              Yes to all of it. The video is yours — share it, keep it, frame
              it on a digital photo display, post it. There's no watermark and
              no subscription.
            </Faq>
            <Faq q="What if I'm not happy?">
              Use your 2 free re-dos first — most people find one they love.
              If we genuinely failed (broken output, wrong content, double
              charge), email{" "}
              <a className="underline" href="mailto:contact@craft-codex.com">
                contact@craft-codex.com
              </a>{" "}
              within 14 days for a refund.
            </Faq>
          </div>
        </div>
      </section>
    </>
  );
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="group p-5 sm:p-6 [&_summary]:cursor-pointer">
      <summary className="flex items-start justify-between gap-4 list-none font-serif text-lg text-neutral-900">
        <span>{q}</span>
        <span className="text-neutral-400 text-2xl leading-none transition-transform group-open:rotate-45 select-none">+</span>
      </summary>
      <div className="mt-3 text-[15px] text-neutral-600 leading-relaxed">{children}</div>
    </details>
  );
}

function VideoCard({ name, caption }: { name: string; caption: string }) {
  return (
    <figure className="rounded-2xl overflow-hidden border border-neutral-200 bg-black">
      <video
        src={`/showcase/${name}.mp4`}
        poster={`/showcase/${name}_after.jpg`}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-auto aspect-[4/5] object-cover bg-black"
      />
      <figcaption className="px-4 py-3 text-sm text-neutral-600 bg-white flex items-center justify-between">
        <span className="italic">{caption}</span>
        <span className="text-xs text-neutral-400">Muted · loops</span>
      </figcaption>
    </figure>
  );
}

function BeforeAfter({ name, caption }: { name: string; caption: string }) {
  return (
    <figure className="rounded-2xl overflow-hidden border border-neutral-200 bg-white">
      <div className="grid grid-cols-2">
        <div className="relative">
          <img
            src={`/showcase/${name}_before.jpg`}
            alt={`${caption} — before restoration`}
            className="w-full h-full object-cover aspect-[4/5]"
            loading="lazy"
          />
          <span className="absolute left-2 top-2 text-[10px] uppercase tracking-widest bg-black/70 text-white px-2 py-0.5 rounded-full">Before</span>
        </div>
        <div className="relative">
          <img
            src={`/showcase/${name}_after.jpg`}
            alt={`${caption} — after restoration`}
            className="w-full h-full object-cover aspect-[4/5]"
            loading="lazy"
          />
          <span className="absolute left-2 top-2 text-[10px] uppercase tracking-widest bg-white/90 text-black px-2 py-0.5 rounded-full">After</span>
        </div>
      </div>
      <figcaption className="px-4 py-3 text-sm text-neutral-600 flex items-center justify-between">
        <span className="italic">{caption}</span>
        <span className="text-xs text-neutral-400">Restored · Colorised</span>
      </figcaption>
    </figure>
  );
}

function Step({ n, title, text }: { n: string; title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6">
      <div className="text-xs text-neutral-400 font-mono">{n}</div>
      <h3 className="mt-2 font-serif text-2xl">{title}</h3>
      <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{text}</p>
    </div>
  );
}

function OccasionCard({
  eyebrow,
  title,
  text,
}: {
  eyebrow: string;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-8">
      <p className="text-xs uppercase tracking-widest text-neutral-500">{eyebrow}</p>
      <h3 className="mt-3 font-serif text-2xl tracking-tight text-neutral-900">
        {title}
      </h3>
      <p className="mt-3 text-[15px] text-neutral-600 leading-relaxed">{text}</p>
    </div>
  );
}
